import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Checkbox } from "../ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  CalendarIcon, Clock, FileText, Users, FolderKanban, FileSpreadsheet, FileType,
  CheckCircle2, Circle, ChevronRight, User, Briefcase, TrendingUp, ArrowRight,
  Pencil, Receipt, Hammer, Trash2, Download, SlidersHorizontal,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { activitiesApi } from "../../lib/api";

export const TidsposterTab = ({
  isAdmin, report, loading, filters, setFilters, startDate, endDate, projects, users,
  selectedEntries, weekGroups, invoiceStats, selectedTotals, collapsedWeeks, exporting,
  setQuickFilter, handleStartDateSelect, handleEndDateSelect,
  handleExportCSV, handleExportPDF, showDetails, showProjectDetails,
  toggleWeekCollapse, handleMarkWeekInvoiced, handleMarkProjectInvoiced,
  toggleEntrySelection, selectNotInvoiced, handleMarkInvoiced,
  handleToggleInvoiced, handleEditEntry, handleDeleteEntry, loadReport,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  return (
    <>
      {/* Header actions */}
      <div className="flex gap-2 items-center flex-wrap">
        {isAdmin && report?.entries?.some(e => !e.invoiced) && (
          <Button onClick={() => {
            const notInvoicedIds = report.entries.filter(e => !e.invoiced).map(e => e.id);
            handleMarkInvoiced(true, notInvoicedIds);
          }}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quick-invoice-all-btn">
            <Receipt className="w-4 h-4" />
            Fakturera alla ({report?.entries?.filter(e => !e.invoiced).length || 0})
          </Button>
        )}
        {selectedEntries.length > 0 && (
          <span className="text-sm text-[#6dbf4a] mr-1">{selectedEntries.length} valda</span>
        )}
        <Button variant="outline" onClick={handleExportCSV} disabled={exporting || !report?.entries?.length}
          className="gap-2 border-white/[0.06] text-white/40 hover:bg-[#333]" data-testid="export-csv-btn">
          <FileSpreadsheet className="w-4 h-4" />
          {selectedEntries.length > 0 ? `CSV (${selectedEntries.length})` : 'CSV'}
        </Button>
        <Button onClick={handleExportPDF} disabled={exporting || !report?.entries?.length}
          className="gap-2 bg-[#6dbf4a] hover:bg-[#5da83d]" data-testid="export-pdf-btn">
          <FileType className="w-4 h-4" />
          {selectedEntries.length > 0 ? `PDF (${selectedEntries.length})` : 'PDF'}
        </Button>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'week', label: 'Denna vecka' },
          { key: 'lastWeek', label: 'Förra veckan' },
          { key: 'month', label: 'Denna månad' },
          { key: 'lastMonth', label: 'Förra månaden' },
        ].map(f => (
          <Button key={f.key} variant="outline" size="sm" onClick={() => setQuickFilter(f.key)}
            className="border-white/[0.06] text-white/40 hover:bg-[#6dbf4a] hover:text-white hover:border-[#6dbf4a]">
            {f.label}
          </Button>
        ))}
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const res = await activitiesApi.syncRates();
              toast.success(res.data.message);
              loadReport();
            } catch (error) { toast.error("Kunde inte synka"); }
          }}
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/20"
            data-testid="sync-rates-btn">
            Synka timpris
          </Button>
        )}
      </div>

      {/* Filters - collapsible on mobile */}
      <div>
        <button onClick={() => setFiltersOpen(!filtersOpen)}
          className="flex items-center gap-2 text-sm text-white/40 hover:text-white/60 transition-colors sm:hidden mb-2">
          <SlidersHorizontal className="w-4 h-4" />
          {filtersOpen ? "Dolj filter" : "Visa filter"}
        </button>
        <Card className={cn("bg-[#0d1218] border-white/[0.06]", !filtersOpen && "hidden sm:block")}>
          <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Single date range picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full col-span-2 justify-start bg-[#080c12] border-white/[0.06] text-white">
                  <CalendarIcon className="mr-2 h-4 w-4 text-white/40" />
                  {startDate && endDate
                    ? `${format(startDate, "d MMM", { locale: sv })} - ${format(endDate, "d MMM", { locale: sv })}`
                    : startDate
                    ? `${format(startDate, "d MMM", { locale: sv })} - ...`
                    : "Valj period"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#0d1218] border-white/[0.06]" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: startDate, to: endDate }}
                  onSelect={(range) => {
                    if (range?.from) handleStartDateSelect(range.from);
                    if (range?.to) handleEndDateSelect(range.to);
                    else if (range?.from && !range?.to) handleEndDateSelect(range.from);
                  }}
                  locale={sv}
                  initialFocus
                  numberOfMonths={1}
                />
              </PopoverContent>
            </Popover>

            <Select value={filters.project_id} onValueChange={(v) => setFilters(prev => ({ ...prev, project_id: v === "all" ? "" : v }))}>
              <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white">
                <FolderKanban className="mr-2 h-4 w-4 text-white/40" />
                <SelectValue placeholder="Projekt" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all">Alla projekt</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.user_id} onValueChange={(v) => setFilters(prev => ({ ...prev, user_id: v === "all" ? "" : v }))}>
              <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white">
                <User className="mr-2 h-4 w-4 text-white/40" />
                <SelectValue placeholder="Anstalld" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all">Alla</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all">Alla</SelectItem>
                <SelectItem value="godkänd">Godkand</SelectItem>
                <SelectItem value="väntande">Vantande</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.invoiced} onValueChange={(v) => setFilters(prev => ({ ...prev, invoiced: v === "all" ? "" : v }))}>
              <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white">
                <SelectValue placeholder="Fakturerad" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all">Alla</SelectItem>
                <SelectItem value="false">Ej fakturerad</SelectItem>
                <SelectItem value="true">Fakturerad</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.is_ata} onValueChange={(v) => setFilters(prev => ({ ...prev, is_ata: v === "all" ? "" : v }))}>
              <SelectTrigger className="bg-[#080c12] border-white/[0.06] text-white" data-testid="filter-ata">
                <SelectValue placeholder="ATA" />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1218] border-white/[0.06]">
                <SelectItem value="all">Alla</SelectItem>
                <SelectItem value="true">Bara ATA</SelectItem>
                <SelectItem value="false">Utan ATA</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div>
        </div>
      ) : report ? (
        <>
          {/* Main Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-[#222] to-[#1a1a1a] border-white/[0.06] hover:border-[#6dbf4a] cursor-pointer transition-all group"
              onClick={() => showDetails("Alla tidsposter", report.entries || [])} data-testid="total-card">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/40 mb-1">Totalt</p>
                    <p className="text-4xl font-bold text-white">{report.total_hours?.toFixed(1)}h</p>
                    {isAdmin && <p className="text-lg text-[#6dbf4a] mt-1">{report.total_amount?.toLocaleString("sv-SE")} kr</p>}
                  </div>
                  <div className="w-16 h-16 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center group-hover:bg-[#6dbf4a]/30 transition-colors">
                    <Clock className="w-8 h-8 text-[#6dbf4a]" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-sm text-white/40 group-hover:text-[#6dbf4a] transition-colors">
                  <span>Visa alla poster</span><ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#222] to-[#1a1a1a] border-white/[0.06] hover:border-amber-500 cursor-pointer transition-all group"
              onClick={() => showDetails("Ej fakturerat", report.entries?.filter(e => !e.invoiced) || [])} data-testid="not-invoiced-card">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/40 mb-1">Ej fakturerat</p>
                    <p className="text-4xl font-bold text-amber-400">{invoiceStats.notInvoicedHours.toFixed(1)}h</p>
                    {isAdmin && <p className="text-lg text-amber-400/70 mt-1">{invoiceStats.notInvoicedAmount.toLocaleString("sv-SE")} kr</p>}
                  </div>
                  <div className="w-16 h-16 rounded-xl bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                    <Circle className="w-8 h-8 text-amber-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-sm text-white/40 group-hover:text-amber-400 transition-colors">
                  <span>Visa ej fakturerade</span><ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#222] to-[#1a1a1a] border-white/[0.06] hover:border-emerald-500 cursor-pointer transition-all group"
              onClick={() => showDetails("Fakturerat", report.entries?.filter(e => e.invoiced) || [])} data-testid="invoiced-card">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/40 mb-1">Fakturerat</p>
                    <p className="text-4xl font-bold text-emerald-400">{invoiceStats.invoicedHours.toFixed(1)}h</p>
                    {isAdmin && <p className="text-lg text-emerald-400/70 mt-1">{invoiceStats.invoicedAmount.toLocaleString("sv-SE")} kr</p>}
                  </div>
                  <div className="w-16 h-16 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-sm text-white/40 group-hover:text-emerald-400 transition-colors">
                  <span>Visa fakturerade</span><ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </CardContent>
            </Card>

            {/* ATA Summary Card */}
            {(() => {
              const ataEntries = report.entries?.filter(e => e.is_ata) || [];
              const ataHours = ataEntries.reduce((s, e) => s + e.hours, 0);
              const ataAmount = ataEntries.reduce((s, e) => s + e.hours * (e.hourly_rate || 0), 0);
              if (ataEntries.length === 0) return null;
              return (
                <Card className="bg-gradient-to-br from-[#222] to-[#1a1a1a] border-orange-500/30 hover:border-orange-500 cursor-pointer transition-all group"
                  onClick={() => showDetails("ATA-poster", ataEntries)} data-testid="ata-card">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white/40 mb-1">ATA</p>
                        <p className="text-4xl font-bold text-orange-400">{ataHours.toFixed(1)}h</p>
                        {isAdmin && <p className="text-lg text-orange-400/70 mt-1">{ataAmount.toLocaleString("sv-SE")} kr</p>}
                      </div>
                      <div className="w-16 h-16 rounded-xl bg-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                        <span className="text-xl font-bold text-orange-400">ATA</span>
                      </div>
                    </div>
                    <div className="flex items-center mt-4 text-sm text-white/40 group-hover:text-orange-400 transition-colors">
                      <span>Visa ATA-poster ({ataEntries.length})</span><ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          {/* Per User Section */}
          {report?.by_user?.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#6dbf4a]" /> Per anstalld
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {report.by_user.map(u => (
                  <Card key={u.user_id} className="bg-[#0d1218] border-white/[0.06] hover:border-[#6dbf4a]/50 cursor-pointer transition-all group"
                    onClick={() => showDetails(u.user_name, report.entries?.filter(e => e.user_id === u.user_id) || [])}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#6dbf4a]/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-[#6dbf4a]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-white truncate">{u.user_name}</p>
                          <p className="text-xl font-bold text-[#6dbf4a]">{u.hours.toFixed(1)}h</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-[#6dbf4a] transition-colors flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Projects Section */}
          {report?.by_project?.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-[#6dbf4a]" /> Projekt
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {report.by_project.map(project => {
                  const projectEntries = report.entries?.filter(e => e.project_id === project.project_id) || [];
                  const invoiced = projectEntries.filter(e => e.invoiced).reduce((s, e) => s + e.hours, 0);
                  const notInvoiced = projectEntries.filter(e => !e.invoiced).reduce((s, e) => s + e.hours, 0);
                  const allInvoiced = projectEntries.length > 0 && projectEntries.every(e => e.invoiced);

                  const activityBreakdown = {};
                  projectEntries.forEach(e => {
                    const aid = e.activity_id;
                    if (!activityBreakdown[aid]) {
                      activityBreakdown[aid] = { name: e.activity_name || "Okand", hours: 0, amount: 0 };
                    }
                    activityBreakdown[aid].hours += e.hours;
                    activityBreakdown[aid].amount += e.hours * (e.hourly_rate || 0);
                  });
                  const activityList = Object.values(activityBreakdown).sort((a, b) => b.hours - a.hours);

                  return (
                    <Card key={project.project_id}
                      className="bg-[#0d1218] border-white/[0.06] hover:border-[#6dbf4a] cursor-pointer transition-all overflow-hidden group"
                      onClick={() => showProjectDetails(project)}>
                      <div className="bg-[#6dbf4a]/10 px-4 py-3 border-b border-white/[0.06]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center">
                              <Briefcase className="w-5 h-5 text-[#6dbf4a]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-white">{project.project_name}</h3>
                              <p className="text-sm text-white/40">{project.client_name}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-[#6dbf4a] transition-colors" />
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-3xl font-bold text-white">{project.hours.toFixed(1)}h</span>
                          {isAdmin && <span className="text-lg text-[#6dbf4a]">{project.amount.toLocaleString("sv-SE")} kr</span>}
                        </div>

                        {activityList.length > 0 && (
                          <div className="space-y-1.5 mb-3 pt-2 border-t border-white/[0.06]">
                            {activityList.map(act => (
                              <div key={act.name} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <Hammer className="w-3 h-3 text-orange-400" />
                                  <span className="text-white/40">{act.name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-white font-medium">{act.hours.toFixed(1)}h</span>
                                  {isAdmin && act.amount > 0 && (
                                    <span className="text-[#6dbf4a] text-xs">{act.amount.toLocaleString("sv-SE")} kr</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-amber-400">{notInvoiced.toFixed(1)}h ej fakt.</span>
                            <span className="text-emerald-400">{invoiced.toFixed(1)}h fakt.</span>
                          </div>
                          <div className="h-2 bg-[#080c12] rounded-full overflow-hidden flex">
                            {project.hours > 0 && (
                              <>
                                <div className="bg-amber-500 h-full" style={{ width: `${(notInvoiced / project.hours) * 100}%` }} />
                                <div className="bg-emerald-500 h-full" style={{ width: `${(invoiced / project.hours) * 100}%` }} />
                              </>
                            )}
                          </div>
                        </div>
                        {isAdmin && notInvoiced > 0 && (
                          <Button size="sm" variant="outline"
                            className="w-full mt-3 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20"
                            onClick={(e) => { e.stopPropagation(); handleMarkProjectInvoiced(project.project_id, true); }}
                            data-testid={`invoice-project-${project.project_id}`}>
                            <Receipt className="w-3.5 h-3.5 mr-2" />
                            Markera allt som fakturerat
                          </Button>
                        )}
                        {isAdmin && allInvoiced && projectEntries.length > 0 && (
                          <Button size="sm" variant="outline"
                            className="w-full mt-3 border-amber-500/40 text-amber-400 hover:bg-amber-500/20"
                            onClick={(e) => { e.stopPropagation(); handleMarkProjectInvoiced(project.project_id, false); }}>
                            Avmarkera fakturerad
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Employee Earnings Chart */}
          {isAdmin && report?.by_user?.length > 0 && (
            <div data-testid="earnings-chart">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#6dbf4a]" /> Intjanade pengar per anstalld
              </h2>
              <Card className="bg-[#0d1218] border-white/[0.06]">
                <CardContent className="pt-6 pb-4">
                  <div className="space-y-3">
                    {[...report.by_user].sort((a, b) => b.amount - a.amount).map((u, index) => {
                      const maxAmount = report.by_user.reduce((max, x) => Math.max(max, x.amount), 1);
                      const pct = maxAmount > 0 ? (u.amount / maxAmount) * 100 : 0;
                      return (
                        <div key={u.user_id} className="flex items-center gap-4">
                          <div className="w-36 flex-shrink-0 text-right">
                            <span className="text-white font-medium text-sm">{u.user_name}</span>
                          </div>
                          <div className="flex-1 relative h-9 bg-[#080c12] rounded-xl overflow-hidden">
                            <div className="h-full rounded-xl transition-all duration-500"
                              style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: index === 0 ? "#6dbf4a" : index === 1 ? "#5da83d" : "#4a8a33" }} />
                            <span className="absolute inset-0 flex items-center px-3 text-white font-bold text-sm" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
                              {u.amount.toLocaleString("sv-SE")} kr
                            </span>
                          </div>
                          <div className="w-20 flex-shrink-0 text-right">
                            <span className="text-white/40 text-sm">{u.hours.toFixed(1)}h</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Invoice Actions Bar */}
          {report?.entries?.length > 0 && (
            <Card className="bg-[#0d1218] border-white/[0.06]">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-white/40 font-medium">Fakturering:</span>
                  <Button variant="outline" size="sm" onClick={selectNotInvoiced}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20" data-testid="select-not-invoiced-btn">
                    Valj ej fakturerade ({report.entries.filter(e => !e.invoiced).length})
                  </Button>

                  {selectedEntries.length > 0 && (
                    <>
                      <div className="h-6 w-px bg-[#3a3a3a]" />
                      <span className="text-white font-medium">
                        {selectedTotals.count} valda &bull; {selectedTotals.hours.toFixed(1)}h
                        {isAdmin && <span className="text-white/40"> ({selectedTotals.amount.toLocaleString("sv-SE")} kr)</span>}
                      </span>
                      <Button size="sm" onClick={() => handleMarkInvoiced(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="mark-invoiced-btn">
                        <CheckCircle2 className="w-4 h-4" /> Markera fakturerad
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleMarkInvoiced(false)} className="border-white/[0.06] text-white/40">
                        Avmarkera
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Week-Grouped Time Entries */}
          {weekGroups.length > 0 && (
            <div data-testid="week-grouped-entries">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#6dbf4a]" /> Tidsposter per vecka
              </h2>

              {weekGroups.map((week) => {
                const isCollapsed = collapsedWeeks[week.key];
                const weekAllInvoiced = week.entries.length > 0 && week.entries.every(e => e.invoiced);
                const weekNotInvoicedCount = week.entries.filter(e => !e.invoiced).length;

                const byEmployee = {};
                week.entries.forEach(e => {
                  const uid = e.user_id;
                  if (!byEmployee[uid]) {
                    byEmployee[uid] = { name: e.user_name, entries: [], totalHours: 0, totalAmount: 0 };
                  }
                  byEmployee[uid].entries.push(e);
                  byEmployee[uid].totalHours += e.hours;
                  byEmployee[uid].totalAmount += e.hours * (e.hourly_rate || 0);
                });
                const employeeList = Object.entries(byEmployee).sort((a, b) => b[1].totalHours - a[1].totalHours);

                return (
                  <div key={week.key} className="mb-4" data-testid={`week-group-${week.year}-${week.weekNum}`}>
                    <div className="flex items-center justify-between bg-[#1f1f1f] px-4 py-3 rounded-t-lg border border-white/[0.06] cursor-pointer"
                      onClick={() => toggleWeekCollapse(week.key)}>
                      <div className="flex items-center gap-3">
                        <div className={cn("transition-transform", isCollapsed ? "" : "rotate-90")}>
                          <ChevronRight className="w-5 h-5 text-white/40" />
                        </div>
                        <div className="w-9 h-9 rounded-xl bg-[#6dbf4a]/20 flex items-center justify-center">
                          <CalendarIcon className="w-5 h-5 text-[#6dbf4a]" />
                        </div>
                        <span className="text-white font-semibold text-lg">{week.label}</span>
                        <Badge variant="outline" className="text-white/40 border-white/[0.06]">{week.entries.length} poster</Badge>
                        <span className="text-white/40 text-sm">{employeeList.length} pers</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {isAdmin && weekNotInvoicedCount > 0 && (
                          <Button size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5"
                            onClick={(e) => { e.stopPropagation(); handleMarkWeekInvoiced(week.entries, true); }}
                            data-testid={`invoice-week-${week.key}`}>
                            <Receipt className="w-3.5 h-3.5" /> Fakturera {weekNotInvoicedCount} poster
                          </Button>
                        )}
                        {isAdmin && weekAllInvoiced && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1" variant="outline">
                            <CheckCircle2 className="w-3 h-3" /> Fakturerad
                          </Badge>
                        )}
                        <span className="text-white font-bold text-lg">{week.totalHours.toFixed(1)}h</span>
                        {isAdmin && <span className="text-[#6dbf4a] font-semibold">{week.totalAmount.toLocaleString("sv-SE")} kr</span>}
                      </div>
                    </div>

                    {!isCollapsed && (
                      <Card className="rounded-t-none border-t-0 bg-[#0d1218] border-white/[0.06]">
                        <div className="divide-y divide-[#3a3a3a]">
                          {employeeList.map(([uid, emp]) => (
                            <div key={uid} className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-[#6dbf4a]/20 flex items-center justify-center">
                                    <User className="w-4 h-4 text-[#6dbf4a]" />
                                  </div>
                                  <span className="text-white font-semibold">{emp.name}</span>
                                  <span className="text-sm text-white/40">{emp.entries.length} poster</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-white font-bold text-lg">{emp.totalHours.toFixed(1)}h</span>
                                  {isAdmin && <span className="text-[#6dbf4a] text-sm font-medium">{emp.totalAmount.toLocaleString("sv-SE")} kr</span>}
                                </div>
                              </div>

                              <div className="space-y-1.5 ml-11">
                                {emp.entries
                                  .sort((a, b) => a.date.localeCompare(b.date))
                                  .map(entry => (
                                    <div key={entry.id}
                                      className={cn(
                                        "flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors",
                                        entry.invoiced ? "bg-emerald-500/5 border border-emerald-500/10" : "bg-[#080c12]"
                                      )}>
                                      <Checkbox checked={selectedEntries.includes(entry.id)} onCheckedChange={() => toggleEntrySelection(entry.id)} className="flex-shrink-0" />
                                      <span className="text-white/40 w-16 flex-shrink-0">{format(new Date(entry.date), "EEE d/M", { locale: sv })}</span>
                                      <div className="flex-1 min-w-0">
                                        <span className="text-white font-medium">{entry.project_name}</span>
                                        <span className="text-white/40 mx-1.5">&middot;</span>
                                        <span className="text-white/40">{entry.activity_name}</span>
                                        {entry.description && (
                                          <span className="text-white/40 ml-2 italic truncate">— {entry.description}</span>
                                        )}
                                      </div>
                                      <span className="text-white font-bold text-base w-12 text-right flex-shrink-0">{entry.hours}h</span>
                                      {isAdmin && (
                                        <span className="text-[#6dbf4a] w-20 text-right flex-shrink-0 text-xs">
                                          {entry.hourly_rate ? (entry.hours * entry.hourly_rate).toLocaleString("sv-SE") + " kr" : "\u2014"}
                                        </span>
                                      )}
                                      {entry.is_ata && (
                                        <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                          ATA
                                        </span>
                                      )}
                                      {isAdmin && (
                                        <button onClick={(e) => handleToggleInvoiced(entry.id, e)}
                                          className={cn(
                                            "flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all",
                                            entry.invoiced
                                              ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                                              : "bg-white/[0.06] text-white/40 hover:bg-amber-500/10 hover:text-amber-400"
                                          )}
                                          data-testid={`toggle-invoiced-${entry.id}`}>
                                          {entry.invoiced
                                            ? <><CheckCircle2 className="w-3.5 h-3.5" /> Fakt.</>
                                            : <><Circle className="w-3.5 h-3.5" /> Ej fakt.</>}
                                        </button>
                                      )}
                                      {isAdmin && (
                                        <>
                                          <Button variant="ghost" size="icon"
                                            onClick={(e) => handleEditEntry(entry, e)}
                                            className="h-7 w-7 text-white/40 hover:text-white flex-shrink-0"
                                            data-testid={`edit-entry-${entry.id}`}>
                                            <Pencil className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button variant="ghost" size="icon"
                                            onClick={(e) => handleDeleteEntry(entry.id, e)}
                                            className="h-7 w-7 text-white/40 hover:text-red-400 flex-shrink-0"
                                            data-testid={`delete-entry-inline-${entry.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </>
  );
};
